import { Component, lazy, type ReactNode, Suspense, useCallback, useMemo, useState } from "react"

import { AlertTriangle, TrendingDown, TrendingUp } from "@/components/icons"
import { useTranslation } from "react-i18next"

import type {
  AccordionElement,
  AlertElement,
  AreaChartElement,
  BarChartElement,
  CheckboxElement,
  DashboardElement,
  GridElement,
  KPICardElement,
  LineChartElement,
  PieChartElement,
  ProgressBarElement,
  ResearchSummaryElement,
  RowElement,
  SelectElement,
  SeparatorElement,
  TableElement,
  TabsElement,
  TextElement,
  TextInputElement,
} from "./catalog"
import type { ValidateTrigger, ValidationCheck } from "../utils/validation"

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import { STATUS_TONES } from "@/components/ui/tone-classes"
import { cn } from "@/lib/utils"

import { ChartTitle } from "./chart-components"
import { formatTickNumber } from "./chart-shared"
import { RepeatScopeProvider } from "./repeat-scope"
import { TileWrapper } from "./tile-wrapper"
import { type DrillContext, DrillThroughSheet } from "../components/drill-through-sheet"
import { KpiSparkline } from "../components/kpi-sparkline"
import { TileEmptyState } from "../components/tile-empty-state"
import { useFieldValidation } from "../hooks/use-field-validation"
import { useResolvedElement, useResolvedValue } from "../hooks/useResolvedElement"
import { useDashState, useDashStateSetter } from "../state/dashboard-state"
import { pickThresholdColor } from "../utils/thresholds"

// §E — split chart imports per type so the bundler emits separate
// chunks. A Bar-only dashboard pays the import cost of Bar + the
// recharts subset Bar uses, not the full chart-components module.
// Each lazy import resolves its file's `default` export.
const LazyBarChart = lazy(() => import("./chart-bar"))
const LazyLineChart = lazy(() => import("./chart-line"))
const LazyAreaChart = lazy(() => import("./chart-area"))
const LazyPieChart = lazy(() => import("./chart-pie"))

class TileErrorBoundary extends Component<
  { elementType: string; errorMessage: string; children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { elementType: string; errorMessage: string; children: ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  render() {
    const { errorMessage, children } = this.props
    const { hasError } = this.state
    if (hasError) {
      return (
        <div className="bg-card border-border flex items-center gap-2 rounded-lg border p-4">
          <AlertTriangle className="text-warning-ink size-4 shrink-0" />
          <span className="text-muted-foreground text-sm">{errorMessage}</span>
        </div>
      )
    }
    return children
  }
}

function ChartFallback() {
  return (
    <div className="bg-card border-border flex h-[280px] items-center justify-center rounded-lg border p-4">
      <div className="border-primary size-6 animate-spin rounded-full border-2 border-t-transparent" />
    </div>
  )
}

function KPICardComponent({ element }: { element: KPICardElement }) {
  const isPositive = element.change !== undefined && element.change >= 0
  const { display, full } = formatKpiValue(element.value)
  // Threshold colouring (Grafana style) wins over the trend-driven
  // emerald/rose default — when an author wires explicit colour
  // brackets they're saying "use these regardless of delta sign".
  // Falls back to delta colouring when no thresholds match (or the
  // value is non-numeric like "Q3 2026").
  const thresholdColor = pickThresholdColor(element.value, element.thresholds)

  return (
    <div
      className={cn(
        "bg-card border-border relative overflow-hidden rounded-xl border p-4 shadow-sm",
        // Left-edge accent stripe via ::before. When a threshold
        // matches we drive its colour via inline style; otherwise
        // pick from the change-direction palette (or neutral primary
        // when no delta).
        "before:absolute before:inset-y-0 before:left-0 before:w-1",
        "before:rounded-l-xl",

        thresholdColor
          ? "before:bg-[var(--kpi-accent)]"
          : element.change === undefined
            ? "before:bg-primary/40"
            : isPositive
              ? "before:bg-success-ink"
              : "before:bg-destructive-ink"
      )}
      style={
        thresholdColor ? ({ "--kpi-accent": thresholdColor } as React.CSSProperties) : undefined
      }
    >
      <p className="text-muted-foreground text-xs leading-tight font-medium tracking-wide break-words uppercase">
        {element.title}
      </p>
      <div className="mt-2 flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
        <span
          title={full !== display ? full : undefined}
          style={thresholdColor ? { color: thresholdColor } : undefined}
          // Show the un-abbreviated number on hover when we shortened it
          // for display. Keeps the full precision discoverable without
          // letting big values wrap and squash the layout.
          className={cn(
            "min-w-0 text-2xl leading-none font-semibold break-words tabular-nums xl:text-3xl",
            // Threshold tint applied via inline style so any hex /
            // hsl() the author writes works without a Tailwind class.
            // No threshold → inherits text-foreground from parent.
            thresholdColor ? "" : "text-foreground"
          )}
        >
          {display}
        </span>
        {element.unit && (
          <span className="text-muted-foreground text-sm font-medium">{element.unit}</span>
        )}
      </div>
      {element.change !== undefined && (
        <div
          className={cn(
            "mt-3 flex items-center gap-1 text-xs font-medium",
            isPositive ? "text-success" : "text-destructive"
          )}
        >
          {isPositive ? <TrendingUp className="size-3.5" /> : <TrendingDown className="size-3.5" />}
          <span className="tabular-nums">
            {isPositive ? "+" : ""}
            {element.change}%
          </span>
          {element.changeLabel && (
            <span className="text-muted-foreground font-normal">{element.changeLabel}</span>
          )}
        </div>
      )}
      {element.sparkline && element.sparkline.length >= 2 && (
        // Trend mini-chart at the bottom of the card. Inherits the
        // change-driven colour (emerald/rose) when a delta is set so
        // the line reinforces the trend direction; falls back to the
        // primary token otherwise. Author-supplied sparkline_color
        // wins over both.
        <div
          className={cn(
            "-mx-1 mt-3 -mb-1",

            element.sparkline_color
              ? ""
              : element.change === undefined
                ? "text-primary/70"
                : isPositive
                  ? "text-success"
                  : "text-destructive"
          )}
        >
          <KpiSparkline color={element.sparkline_color} values={element.sparkline} />
        </div>
      )}
    </div>
  )
}

/**
 * KPI values come in two shapes:
 *
 * - Numbers: the AI emitted the raw value (e.g. 541524042). The card
 *   would display "541524042" raw, blowing the tile width and forcing
 *   the line to wrap into "541 524\n042". Format compactly ("541.5 M")
 *   with a hover-title that surfaces the full sv-SE-localised number.
 * - Strings: the AI emitted a pre-formatted display value (e.g.
 *   "541 524 042" or "100.39"). Trust it but disable wrap so the layout
 *   doesn't break. If the string parses cleanly as a large number we
 *   ALSO compact-format it; otherwise we keep the original (a string
 *   like "Q3 2026" stays as "Q3 2026").
 */
function formatKpiValue(value: string | number): { display: string; full: string } {
  if (typeof value === "number" && Number.isFinite(value)) {
    const full = value.toLocaleString("sv-SE")
    if (Math.abs(value) >= 10_000) {
      return { display: formatTickNumber(value), full }
    }
    return { display: full, full }
  }
  if (typeof value !== "string") {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const stringified = value == null ? "" : String(value)
    return { display: stringified, full: stringified }
  }
  // Try to parse a locale-formatted number like "541 524 042" or
  // "541.524.042" or "541,524,042" → compact when large.

  const stripped = value.replace(/[\s ]/g, "").replace(/,/g, ".")
  const numeric = stripped.match(/^-?\d+(\.\d+)?$/) ? Number(stripped) : NaN
  if (Number.isFinite(numeric) && Math.abs(numeric) >= 10_000) {
    return { display: formatTickNumber(numeric), full: value }
  }
  return { display: value, full: value }
}

function TableComponent({ element }: { element: TableElement }) {
  const [drillContext, setDrillContext] = useState<DrillContext | null>(null)

  if (!Array.isArray(element.rows) || element.rows.length === 0) {
    return <TileEmptyState title={element.title} />
  }
  const drill = element.drillThrough
  return (
    <>
      <div className="bg-card border-border overflow-hidden rounded-xl border shadow-sm">
        {element.title && (
          <div className="border-border/60 border-b px-4 pt-4 pb-3">
            <ChartTitle title={element.title} />
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 sticky top-0 z-10 backdrop-blur">
              <tr>
                {element.columns.map((col) => (
                  <th
                    key={col.key}
                    className={cn(
                      "text-muted-foreground border-border/60 border-b px-4 py-2.5 text-left text-[11px] font-semibold tracking-wider uppercase",
                      col.align === "center" && "text-center",
                      col.align === "right" && "text-right tabular-nums"
                    )}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {element.rows.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  data-testid={drill ? `dash-drill-row-${rowIndex}` : undefined}
                  className={cn(
                    "border-border/40 border-b transition-colors last:border-0",
                    // Zebra striping is subtle — uses muted at 30% so the
                    // difference reads as "alternating row" without
                    // overwhelming numeric scanning.
                    rowIndex % 2 === 1 && "bg-muted/30",
                    drill ? "hover:bg-accent/60 cursor-pointer" : "hover:bg-accent/50"
                  )}
                  onClick={drill ? () => setDrillContext({ drill, row }) : undefined}
                >
                  {element.columns.map((col) => (
                    <td
                      key={col.key}
                      className={cn(
                        "px-4 py-2.5",
                        col.align === "center" && "text-center",
                        col.align === "right" && "text-right tabular-nums"
                      )}
                    >
                      {row[col.key] ?? ""}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <DrillThroughSheet context={drillContext} onClose={() => setDrillContext(null)} />
    </>
  )
}

function TextComponent({ element }: { element: TextElement }) {
  const variantClasses = {
    heading: "text-xl font-bold",
    subheading: "text-lg font-semibold",
    body: "text-sm",
    caption: "text-xs text-muted-foreground",
  }

  return (
    <p className={cn("text-foreground", variantClasses[element.variant ?? "body"])}>
      {element.content}
    </p>
  )
}

function SeparatorComponent({ element }: { element: SeparatorElement }) {
  if (element.label) {
    return (
      <div className="flex items-center gap-3 py-2">
        <div className="bg-border h-px flex-1" />
        <span className="text-muted-foreground text-xs font-medium">{element.label}</span>
        <div className="bg-border h-px flex-1" />
      </div>
    )
  }
  return <hr className="border-border my-2" />
}

function AlertComponent({ element }: { element: AlertElement }) {
  const variantStyles = {
    info: STATUS_TONES.info.soft,
    warning: "border-warning-ink bg-warning/30 text-warning-ink ",
    success: STATUS_TONES.success.soft,
    error: STATUS_TONES.destructive.soft,
  }
  const variant = element.variant ?? "info"

  return (
    <div className={cn("rounded-lg border p-4", variantStyles[variant])}>
      {element.title && <p className="mb-1 text-sm font-semibold">{element.title}</p>}
      <p className="text-sm">{element.message}</p>
    </div>
  )
}

function ResearchSummaryComponent({ element }: { element: ResearchSummaryElement }) {
  const { t } = useTranslation()
  const content = typeof element.content === "string" ? element.content.trim() : ""
  const sources = Array.isArray(element.sources) ? element.sources : []
  return (
    <div className="border-border bg-card rounded-lg border p-4">
      {element.title && (
        <p className="text-foreground mb-2 text-sm font-semibold">{element.title}</p>
      )}
      {content ? (
        <p className="text-foreground text-sm whitespace-pre-wrap">{content}</p>
      ) : (
        <p className="text-muted-foreground text-sm italic">
          {t("dash.research.empty", { defaultValue: "Refresh to load AI research." })}
        </p>
      )}
      {sources.length > 0 && (
        <div className="mt-3 flex flex-col gap-1">
          <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
            {t("dash.research.sources", { defaultValue: "Sources" })}
          </p>
          {sources.map((source, i) => (
            <a
              key={`${source.url}-${i}`}
              className="text-primary truncate text-xs hover:underline"
              href={source.url}
              rel="noopener noreferrer"
              target="_blank"
            >
              {source.title || source.url}
            </a>
          ))}
        </div>
      )}
    </div>
  )
}

function ProgressBarComponent({ element }: { element: ProgressBarElement }) {
  const max = element.max ?? 100
  const pct = Math.min(100, Math.max(0, (element.value / max) * 100))

  return (
    <div className="bg-card border-border rounded-lg border p-4">
      {element.label && (
        <div className="mb-2 flex items-baseline justify-between">
          <span className="text-foreground text-sm font-medium">{element.label}</span>
          <span className="text-muted-foreground text-sm">
            {element.value}/{max}
          </span>
        </div>
      )}
      <div className="bg-muted h-2.5 w-full overflow-hidden rounded-full">
        <div
          className="h-full rounded-full transition-[width]"
          style={{
            width: `${pct}%`,
            backgroundColor: element.color ?? "hsl(var(--primary-ink))",
          }}
        />
      </div>
    </div>
  )
}

function GridComponent({
  element,
  elementPath,
  selectedTileIndex,
  onSelectTile,
  indexCounter,
}: {
  element: GridElement
  elementPath?: string
  selectedTileIndex?: number | null
  onSelectTile?: (index: number) => void
  indexCounter?: { current: number }
}) {
  // A removed tile must not leave a blank grid track behind. Keep the
  // configured maximum, but collapse to the number of remaining children.
  const cols = Math.min(element.columns ?? 2, Math.max(1, element.children.length))
  const gridClass = {
    1: "grid-cols-1",
    2: "grid-cols-1 md:grid-cols-2",
    3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 md:grid-cols-2 lg:grid-cols-4",
  }[cols]

  return (
    <div className={cn("grid", gridClass)} style={{ gap: element.gap ?? 16 }}>
      {element.children.map((child, i) => (
        <DashboardElementRenderer
          key={i}
          element={child}
          elementPath={elementPath ? `${elementPath}.children[${i}]` : `children[${i}]`}
          indexCounter={indexCounter}
          selectedTileIndex={selectedTileIndex}
          onSelectTile={onSelectTile}
        />
      ))}
    </div>
  )
}

function RowComponent({
  element,
  elementPath,
  selectedTileIndex,
  onSelectTile,
  indexCounter,
}: {
  element: RowElement
  elementPath?: string
  selectedTileIndex?: number | null
  onSelectTile?: (index: number) => void
  indexCounter?: { current: number }
}) {
  return (
    // Mobile-responsive: stack vertically below the md-breakpoint
    // (768px). Children get `basis-full` so they fill the row on
    // small screens; `md:flex-1 md:basis-0` restores the equal-share
    // horizontal layout on tablets and up. Avoids overflowing
    // 3-up KPI rows on phones.
    <div
      className="flex flex-col items-stretch md:flex-row md:flex-wrap"
      style={{ gap: element.gap ?? 16 }}
    >
      {element.children.map((child, i) => (
        <div key={i} className="min-w-0 basis-full md:flex-1 md:basis-0">
          <DashboardElementRenderer
            element={child}
            elementPath={elementPath ? `${elementPath}.children[${i}]` : `children[${i}]`}
            indexCounter={indexCounter}
            selectedTileIndex={selectedTileIndex}
            onSelectTile={onSelectTile}
          />
        </div>
      ))}
    </div>
  )
}

function TabsComponent({
  element,
  elementPath,
  selectedTileIndex,
  onSelectTile,
  indexCounter,
}: {
  element: TabsElement
  elementPath?: string
  selectedTileIndex?: number | null
  onSelectTile?: (index: number) => void
  indexCounter?: { current: number }
}) {
  const defaultValue = element.defaultValue ?? element.tabs[0]?.value

  return (
    <Tabs defaultValue={defaultValue}>
      {/* Grafana-flavoured active bar: minimal triggers, bottom border
          on the row, accent-coloured underline on the active trigger.
          Avoids the default Radix/shadcn pill style which felt too
          "form widget" for a dashboard surface. */}
      <TabsList className="border-border h-auto justify-start gap-1 rounded-none border-b bg-transparent p-0">
        {element.tabs.map((tab) => (
          <TabsTrigger
            key={tab.value}
            value={tab.value}
            className={cn(
              "data-[state=active]:bg-transparent data-[state=active]:shadow-none",
              "data-[state=active]:border-primary data-[state=active]:text-foreground",
              "text-muted-foreground hover:text-foreground",
              "relative -mb-px rounded-none border-b-2 border-transparent px-3 py-2 text-sm font-medium transition-colors"
            )}
          >
            {tab.label}
          </TabsTrigger>
        ))}
      </TabsList>
      {element.tabs.map((tab, tabIdx) => (
        <TabsContent key={tab.value} value={tab.value}>
          <div className="flex flex-col gap-4">
            {tab.children.map((child, i) => {
              const childPath = elementPath
                ? `${elementPath}.tabs[${tabIdx}].children[${i}]`
                : `tabs[${tabIdx}].children[${i}]`
              return (
                <DashboardElementRenderer
                  key={i}
                  element={child}
                  elementPath={childPath}
                  indexCounter={indexCounter}
                  selectedTileIndex={selectedTileIndex}
                  onSelectTile={onSelectTile}
                />
              )
            })}
          </div>
        </TabsContent>
      ))}
    </Tabs>
  )
}

function AccordionComponent({
  element,
  elementPath,
  selectedTileIndex,
  onSelectTile,
  indexCounter,
}: {
  element: AccordionElement
  elementPath?: string
  selectedTileIndex?: number | null
  onSelectTile?: (index: number) => void
  indexCounter?: { current: number }
}) {
  return (
    <Accordion defaultValue={["item-0"]} type="multiple">
      {element.items.map((item, i) => (
        <AccordionItem key={i} value={`item-${i}`}>
          <AccordionTrigger>{item.title}</AccordionTrigger>
          <AccordionContent>
            <div className="flex flex-col gap-4">
              {item.children.map((child, j) => {
                const childPath = elementPath
                  ? `${elementPath}.items[${i}].children[${j}]`
                  : `items[${i}].children[${j}]`
                return (
                  <DashboardElementRenderer
                    key={j}
                    element={child}
                    elementPath={childPath}
                    indexCounter={indexCounter}
                    selectedTileIndex={selectedTileIndex}
                    onSelectTile={onSelectTile}
                  />
                )
              })}
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  )
}

function SelectComponent({ element }: { element: SelectElement }) {
  const setState = useDashStateSetter()
  const currentValue = useDashState(element.stateKey) as string | undefined
  const value = currentValue ?? element.defaultValue ?? ""

  const handleChange = useCallback(
    (val: string) => setState(element.stateKey, val),
    [setState, element.stateKey]
  )

  return (
    <div className="bg-card border-border rounded-lg border p-4">
      {element.label && (
        <Label className="text-muted-foreground mb-2 block text-sm">{element.label}</Label>
      )}
      <Select value={value} onValueChange={handleChange}>
        <SelectTrigger>
          <SelectValue placeholder={element.placeholder ?? "Select..."} />
        </SelectTrigger>
        <SelectContent>
          {element.options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function CheckboxComponent({ element }: { element: CheckboxElement }) {
  const setState = useDashStateSetter()
  const currentValue = useDashState(element.stateKey) as boolean | undefined
  const checked = currentValue ?? element.defaultValue ?? false

  const handleChange = useCallback(
    (val: boolean | "indeterminate") => setState(element.stateKey, val === true),
    [setState, element.stateKey]
  )

  return (
    <div className="bg-card border-border flex items-center gap-2 rounded-lg border p-4">
      <Checkbox checked={checked} id={element.stateKey} onCheckedChange={handleChange} />
      <Label className="text-foreground text-sm" htmlFor={element.stateKey}>
        {element.label}
      </Label>
    </div>
  )
}

function TextInputComponent({ element }: { element: TextInputElement }) {
  const setState = useDashStateSetter()
  const currentValue = useDashState(element.stateKey) as string | undefined
  const value = currentValue ?? element.defaultValue ?? ""

  // §2.6 — field-level validation: runs declared `checks` at the
  // configured `validateOn` trigger and surfaces inline error text.
  const validation = useFieldValidation(value, {
    checks: (element as TextInputElement & { checks?: ValidationCheck[] }).checks,
    validateOn: (element as TextInputElement & { validateOn?: ValidateTrigger }).validateOn,
  })

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setState(element.stateKey, e.target.value)
      validation.onChange(e.target.value)
    },
    [setState, element.stateKey, validation]
  )

  const hasError = validation.errors.length > 0

  return (
    <div className="bg-card border-border rounded-lg border p-4">
      {element.label && (
        <Label className="text-muted-foreground mb-2 block text-sm">{element.label}</Label>
      )}
      <Input
        aria-invalid={hasError ? "true" : "false"}
        className={hasError ? "border-destructive focus-visible:ring-destructive" : undefined}
        placeholder={element.placeholder}
        value={value}
        onBlur={validation.onBlur}
        onChange={handleChange}
      />
      {hasError && <p className="text-destructive mt-1 text-xs">{validation.errors[0]}</p>}
    </div>
  )
}

type DashboardElementRendererProps = {
  element: DashboardElement
  flatIndex?: number
  /** Dotted path into the spec (e.g. "children[0].children[1]") — matches
   * query_binding.element_path so the tile badge can look up its binding. */
  elementPath?: string
  selectedTileIndex?: number | null
  onSelectTile?: (index: number) => void
  indexCounter?: { current: number }
}

export function DashboardElementRenderer(props: DashboardElementRendererProps) {
  const { element, flatIndex, indexCounter } = props

  // Resolve expressions ($state, $template, $filter, $computed, $cond) against dashboard state.
  // The hooks below run unconditionally — visibility-based skipping is
  // delegated to a child component (RenderedTile) so React's hook-order
  // contract is preserved when a `visible` binding flips true→false.
  const resolved = useResolvedElement(element as Record<string, unknown>)

  const isLayoutType =
    element.type === "Grid" ||
    element.type === "Row" ||
    element.type === "Tabs" ||
    element.type === "Accordion" ||
    element.type === "Separator" ||
    element.type === "Repeat" ||
    element.type === "Tooltip" ||
    element.type === "Heading" ||
    element.type === "Badge" ||
    element.type === "Avatar"

  // Handle visible: hide element if resolved to false.
  // The early-return below is safe because no hooks run after this point —
  // RenderedTile owns the remaining hooks and only mounts when visible.
  if (resolved.visible === false) {
    if (!isLayoutType && indexCounter) indexCounter.current++
    return null
  }

  const currentIndex = isLayoutType ? undefined : (flatIndex ?? indexCounter?.current)
  if (!isLayoutType && indexCounter) {
    indexCounter.current++
  }

  return (
    <RenderedTile
      currentIndex={currentIndex}
      isLayoutType={isLayoutType}
      resolved={resolved}
      {...props}
    />
  )
}

interface RenderedTileProps extends DashboardElementRendererProps {
  resolved: Record<string, unknown>
  isLayoutType: boolean
  currentIndex: number | undefined
}

function RenderedTile({
  element,
  elementPath,
  selectedTileIndex,
  onSelectTile,
  indexCounter,
  resolved,
  isLayoutType,
  currentIndex,
}: RenderedTileProps) {
  const { t } = useTranslation()

  const rendered = useMemo(() => {
    switch (element.type) {
      case "BarChart":
        return (
          <Suspense fallback={<ChartFallback />}>
            <LazyBarChart element={resolved as unknown as BarChartElement} />
          </Suspense>
        )
      case "LineChart":
        return (
          <Suspense fallback={<ChartFallback />}>
            <LazyLineChart element={resolved as unknown as LineChartElement} />
          </Suspense>
        )
      case "AreaChart":
        return (
          <Suspense fallback={<ChartFallback />}>
            <LazyAreaChart element={resolved as unknown as AreaChartElement} />
          </Suspense>
        )
      case "PieChart":
        return (
          <Suspense fallback={<ChartFallback />}>
            <LazyPieChart element={resolved as unknown as PieChartElement} />
          </Suspense>
        )
      case "KPICard":
        return <KPICardComponent element={resolved as unknown as KPICardElement} />
      case "Table":
        return <TableComponent element={resolved as unknown as TableElement} />
      case "Text":
        return <TextComponent element={resolved as unknown as TextElement} />
      case "Separator":
        return <SeparatorComponent element={resolved as unknown as SeparatorElement} />
      case "Alert":
        return <AlertComponent element={resolved as unknown as AlertElement} />
      case "ProgressBar":
        return <ProgressBarComponent element={resolved as unknown as ProgressBarElement} />
      case "ResearchSummary":
        return <ResearchSummaryComponent element={resolved as unknown as ResearchSummaryElement} />
      case "Grid":
        return (
          <GridComponent
            element={element as GridElement}
            elementPath={elementPath}
            indexCounter={indexCounter}
            selectedTileIndex={selectedTileIndex}
            onSelectTile={onSelectTile}
          />
        )
      case "Row":
        return (
          <RowComponent
            element={element as RowElement}
            elementPath={elementPath}
            indexCounter={indexCounter}
            selectedTileIndex={selectedTileIndex}
            onSelectTile={onSelectTile}
          />
        )
      case "Tabs":
        return (
          <TabsComponent
            element={element as TabsElement}
            elementPath={elementPath}
            indexCounter={indexCounter}
            selectedTileIndex={selectedTileIndex}
            onSelectTile={onSelectTile}
          />
        )
      case "Accordion":
        return (
          <AccordionComponent
            element={element as AccordionElement}
            elementPath={elementPath}
            indexCounter={indexCounter}
            selectedTileIndex={selectedTileIndex}
            onSelectTile={onSelectTile}
          />
        )
      case "Select":
        return <SelectComponent element={element as SelectElement} />
      case "Checkbox":
        return <CheckboxComponent element={element as CheckboxElement} />
      case "TextInput":
        return <TextInputComponent element={element as TextInputElement} />
      case "Repeat":
        return (
          <RepeatComponent
            element={element as { source: unknown; item: unknown; keyField?: string }}
            elementPath={elementPath}
            indexCounter={indexCounter}
            selectedTileIndex={selectedTileIndex}
            onSelectTile={onSelectTile}
          />
        )
      case "Heading":
        return (
          <HeadingComponent
            element={resolved as unknown as { text: string; level?: 1 | 2 | 3 | 4 | 5 | 6 }}
          />
        )
      case "Badge":
        return (
          <BadgeComponent
            element={
              resolved as unknown as {
                text: string
                variant?: "default" | "secondary" | "destructive" | "outline"
              }
            }
          />
        )
      case "Avatar":
        return (
          <AvatarComponent
            element={
              resolved as unknown as { name: string; src?: string; size?: "sm" | "md" | "lg" }
            }
          />
        )
      case "Tooltip":
        return (
          <TooltipComponent
            element={element as { content: unknown; child: DashboardElement }}
            elementPath={elementPath}
            indexCounter={indexCounter}
            selectedTileIndex={selectedTileIndex}
            onSelectTile={onSelectTile}
          />
        )
      default:
        return null
    }
  }, [element, resolved, elementPath, indexCounter, selectedTileIndex, onSelectTile])

  // Human-readable tile name for the keyboard "Select tile" button.
  const labelSource = resolved as unknown as Record<string, unknown>
  const labelCandidate = [labelSource.title, labelSource.label, labelSource.text].find(
    (v): v is string => typeof v === "string" && v.trim().length > 0
  )
  const tileLabel = labelCandidate?.trim() ?? element.type

  if (isLayoutType) {
    return (
      <TileErrorBoundary
        elementType={element.type}
        errorMessage={t("dash.dashboard.component_error", { type: element.type })}
      >
        {rendered}
      </TileErrorBoundary>
    )
  }

  return (
    <TileErrorBoundary
      elementType={element.type}
      errorMessage={t("dash.dashboard.component_error", { type: element.type })}
    >
      <TileWrapper
        elementPath={elementPath}
        elementType={element.type}
        index={currentIndex}
        isSelected={currentIndex !== undefined && selectedTileIndex === currentIndex}
        tileLabel={tileLabel}
        onSelect={onSelectTile ? (idx) => onSelectTile(idx === -1 ? -1 : idx) : undefined}
      >
        {rendered}
      </TileWrapper>
    </TileErrorBoundary>
  )
}

/**
 * Render a Repeat element — one row per item in the resolved source
 * array. Each row is wrapped in a RepeatScopeProvider so descendant
 * elements can read `{$item: "..."}` and `{$index: true}` via
 * useResolvedElement.
 */
function RepeatComponent({
  element,
  elementPath,
  selectedTileIndex,
  onSelectTile,
  indexCounter,
}: {
  element: { source: unknown; item: unknown; keyField?: string }
  elementPath?: string
  selectedTileIndex?: number | null
  onSelectTile?: (index: number) => void
  indexCounter?: { current: number }
}) {
  const source = useResolvedValue(element.source)
  if (!Array.isArray(source) || source.length === 0) return null

  // The item template is a DashboardElement value the AI emitted
  // verbatim. We pass it unmodified to each child renderer; expression
  // resolution happens *inside* useResolvedElement once the
  // RepeatScopeProvider wraps the subtree.
  const template = element.item as DashboardElement

  return (
    <>
      {source.map((item, index) => {
        const key =
          element.keyField && item && typeof item === "object" && !Array.isArray(item)
            ? String((item as Record<string, unknown>)[element.keyField])
            : index
        return (
          <RepeatScopeProvider key={key} index={index} item={item}>
            <DashboardElementRenderer
              element={template}
              elementPath={elementPath ? `${elementPath}.item[${index}]` : undefined}
              indexCounter={indexCounter}
              selectedTileIndex={selectedTileIndex}
              onSelectTile={onSelectTile}
            />
          </RepeatScopeProvider>
        )
      })}
    </>
  )
}

// ── shadcn primitives (§3.2) ────────────────────────────────────────

function HeadingComponent({
  element,
}: {
  element: { text: string; level?: 1 | 2 | 3 | 4 | 5 | 6 }
}) {
  const level = element.level ?? 2
  const sizeClass =
    level === 1
      ? "text-2xl font-semibold"
      : level === 2
        ? "text-xl font-semibold"
        : level === 3
          ? "text-lg font-medium"
          : level === 4
            ? "text-base font-medium"
            : "text-sm font-medium"
  // Render the matching semantic tag for accessibility.

  const Tag = `h${level}` as const
  return <Tag className={cn("text-foreground", sizeClass)}>{element.text}</Tag>
}

function BadgeComponent({
  element,
}: {
  element: { text: string; variant?: "default" | "secondary" | "destructive" | "outline" }
}) {
  return <Badge variant={element.variant}>{element.text}</Badge>
}

function AvatarComponent({
  element,
}: {
  element: { name: string; src?: string; size?: "sm" | "md" | "lg" }
}) {
  const initials = element.name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase()
  const sizeClass =
    element.size === "sm"
      ? "size-6 text-xs"
      : element.size === "lg"
        ? "size-12 text-base"
        : "size-8 text-sm"
  return (
    <Avatar className={sizeClass}>
      {element.src && <AvatarImage alt={element.name} src={element.src} />}
      <AvatarFallback>{initials || "?"}</AvatarFallback>
    </Avatar>
  )
}

function TooltipComponent({
  element,
  elementPath,
  indexCounter,
  selectedTileIndex,
  onSelectTile,
}: {
  element: { content: unknown; child: DashboardElement }
  elementPath?: string
  indexCounter?: { current: number }
  selectedTileIndex?: number | null
  onSelectTile?: (index: number) => void
}) {
  // The wrapped child resolves its own props via DashboardElementRenderer;
  // we only resolve the tooltip text via useResolvedValue.
  const resolvedContent = useResolvedValue(element.content)
  const text = typeof resolvedContent === "string" ? resolvedContent : String(resolvedContent ?? "")
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span>
            <DashboardElementRenderer
              element={element.child}
              elementPath={elementPath ? `${elementPath}.child` : undefined}
              indexCounter={indexCounter}
              selectedTileIndex={selectedTileIndex}
              onSelectTile={onSelectTile}
            />
          </span>
        </TooltipTrigger>
        <TooltipContent>{text}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
